import {
	Alert,
	Card,
	Modal,
	Stack,
	Text,
	ThemeIcon,
	Title,
	List,
	useComputedColorScheme,
} from "@mantine/core";
import { IconPhoneCall } from "@tabler/icons-react";
import type { ReactNode } from "react";

function InstructionListItemWrapper({
	number,
	totalSteps,
	children,
}: {
	number: number;
	totalSteps: number;
	children: ReactNode;
}) {
	return (
		<List.Item
			/* Adding explicit role because in Safari `listStyle: "none"` removes the list's semantic meaning */
			// biome-ignore lint/a11y/useSemanticElements: <explanation>
			role="listitem"
			style={{ listStyle: "none", margin: ".25rem 0" }}
		>
			<Stack style={{ flexDirection: "row" }} gap="s" align="flex-start">
				<ThemeIcon
					size={22}
					radius="xl"
					variant="filled"
					color="blue"
					aria-label={`Step ${number} of ${totalSteps}`}
				>
					<Text size="xs" fw={700} c="white" aria-hidden>
						{number}
					</Text>
				</ThemeIcon>

				{children}
			</Stack>
		</List.Item>
	);
}

type IvrInstructionsModalProps = {
	opened: boolean;
	onClose: () => void;
	additionalActions?: ReactNode;
};

export function IvrInstructionsModal({
	opened,
	onClose,
	additionalActions,
}: IvrInstructionsModalProps) {
	const PHONE_NUMBER = "844-749-1268";
	const colorScheme = useComputedColorScheme("light", {
		getInitialValueInEffect: true,
	});
	const isDark = colorScheme === "dark";

	const modalTitle = "Check in and out by phone";
	const ivrInstructions = {
		title: "You must call from the client's phone",
		description:
			"The call must come from the client phone number saved in their profile. Calling from your phone will not work.",
	};
	const checkInHeader = {
		title: "Check in",
		description: `Call ${PHONE_NUMBER} from the client's phone`,
	};
	const checkOutHeader = {
		title: "Check out",
		description: `Call ${PHONE_NUMBER} from the client's phone`,
	};
	const checkInInstructions = [
		{
			step: 1,
			text: "Enter your date of birth as 6 digits (MMDDYY). For example, January 5, 1990 is 010590.",
		},
		{
			step: 2,
			text: "Press 1 to check in",
		},
	];
	const checkOutInstructions = [
		{
			step: 1,
			text: "Enter your date of birth as 6 digits (MMDDYY). For example, January 5, 1990 is 010590.",
		},
		{
			step: 2,
			text: "Press 1 to check out",
		},
		{
			step: 3,
			text: "Answer each care sheet task: press 1 for completed or 2 for not completed",
			additionalInfo:
				"If Other is selected, state the task. The phone system will also request transportation details for WV ADW and PC, and environmental task times for WV PC.",
		},
		{
			step: 4,
			text: "State any needed comment",
		},
		{
			step: 5,
			text: "Record yourself stating your name",
		},
		{
			step: 6,
			text: "Record the client stating their name",
		},
	];

	const buildOrderedList = (
		arr: { step: number; text: string; additionalInfo?: string }[],
	) => {
		return (
			/* Adding explicit role because in Safari `listStyle: "none"` removes the list's semantic meaning */
			// biome-ignore lint/a11y/useSemanticElements: <explanation>
			<List type="ordered" role="list">
				{arr.map((item, _i, arr) => {
					return (
						<InstructionListItemWrapper
							number={item.step}
							key={item.text}
							totalSteps={arr.length}
						>
							<Text size="s" style={{ flexDirection: "column" }}>
								{item.text}

								{item.additionalInfo && (
									<Text size="xs" fs="italic">
										{item.additionalInfo}
									</Text>
								)}
							</Text>
						</InstructionListItemWrapper>
					);
				})}
			</List>
		);
	};
	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title={modalTitle}
			closeButtonProps={{ "aria-label": "Close" }}
			fullScreen
			centered
			styles={{
				header: {
					paddingTop: "var(--mantine-spacing-xl)",
					paddingLeft: "var(--mantine-spacing-sm)",
					paddingRight: "var(--mantine-spacing-sm)",
				},
				title: { fontWeight: 700, fontSize: "var(--mantine-font-size-lg)" },
				body: {
					overflowY: "auto",
					paddingTop: "calc(var(--mantine-spacing-lg) )",
					paddingBottom: "var(--mantine-spacing-xl)",
					paddingLeft: "var(--mantine-spacing-sm)",
					paddingRight: "var(--mantine-spacing-sm)",
				},
			}}
		>
			<Stack gap="lg">
				<Alert
					icon={<IconPhoneCall size={22} style={{ color: "#b67f00" }} />}
					color="yellow"
					variant="light"
					title={
						<Title
							order={2}
							fw={800}
							size="xl"
							style={{ color: "#b67f00", margin: "0" }}
						>
							{ivrInstructions.title}
						</Title>
					}
				>
					<Text size="sm">{ivrInstructions.description}</Text>
				</Alert>

				<Card
					withBorder
					radius="md"
					p="md"
					bg={isDark ? "dark.6" : "blue.0"}
					style={{
						borderColor: isDark
							? "var(--mantine-color-blue-7)"
							: "var(--mantine-color-blue-3)",
					}}
				>
					<Stack gap="sm">
						<Title
							order={3}
							fw={700}
							size="xl"
							c={isDark ? "blue.3" : "blue.8"}
						>
							{checkInHeader.title}
						</Title>
						<Text fw={600} size="sm">
							{checkInHeader.description}
						</Text>
						{buildOrderedList(checkInInstructions)}
					</Stack>
				</Card>

				<Card
					withBorder
					radius="md"
					p="md"
					bg={isDark ? "dark.6" : "cyan.0"}
					style={{
						borderColor: isDark
							? "var(--mantine-color-cyan-7)"
							: "var(--mantine-color-cyan-3)",
					}}
				>
					<Stack gap="sm">
						<Title
							order={3}
							fw={700}
							size="xl"
							c={isDark ? "cyan.3" : "cyan.9"}
						>
							{checkOutHeader.title}
						</Title>
						<Text fw={600} size="sm">
							{checkOutHeader.description}
						</Text>
						{buildOrderedList(checkOutInstructions)}
					</Stack>
				</Card>

				{additionalActions && <Stack gap="sm">{additionalActions}</Stack>}
			</Stack>
		</Modal>
	);
}
